import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Newspaper, Sparkles, Calendar, ArrowRight } from 'lucide-react';

const formatDate = (iso, lang) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(
      lang.startsWith('pt') ? 'pt-BR' : 'en-US',
      { day: '2-digit', month: 'short', year: 'numeric' }
    );
  } catch {
    return '';
  }
};

const buildImageUrl = (apiUrl, coverUrl) => {
  if (!coverUrl) return null;
  return coverUrl.startsWith('http') ? coverUrl : `${apiUrl}${coverUrl}`;
};

const PostCard = ({ post, apiUrl, onOpen, t }) => {
  const isProject = post.category === 'project';
  const badgeClass = isProject
    ? 'bg-amber-100 text-amber-800 border-amber-200'
    : 'bg-teal-100 text-teal-800 border-teal-200';
  const badgeIcon = isProject ? <Sparkles size={12} aria-hidden="true" /> : <Newspaper size={12} aria-hidden="true" />;
  const badgeLabel = isProject ? t('news.badgeProject') : t('news.badgeNews');
  const img = buildImageUrl(apiUrl, post.cover_url);
  const { i18n } = useTranslation();

  return (
    <button
      type="button"
      onClick={() => onOpen(post)}
      data-testid={`news-card-${post.id}`}
      className="text-left bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl border border-gray-100 hover:border-teal-200 transition-all duration-300 flex flex-col group focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
    >
      <div className="relative h-48 bg-gray-100 overflow-hidden">
        {img ? (
          <img
            src={img}
            alt={post.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-teal-100 to-blue-100">
            <Newspaper size={40} className="text-teal-500" aria-hidden="true" />
          </div>
        )}
        <span
          className={`absolute top-3 left-3 inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider border ${badgeClass}`}
        >
          {badgeIcon}
          {badgeLabel}
        </span>
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
          <Calendar size={12} aria-hidden="true" />
          <span>{formatDate(post.created_at, i18n.language)}</span>
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">
          {post.title}
        </h3>
        <p className="text-sm text-gray-600 line-clamp-3 flex-1">{post.summary}</p>
        <span className="mt-4 inline-flex items-center gap-1 text-teal-600 font-semibold text-sm group-hover:gap-2 transition-all">
          {t('news.readMore')}
          <ArrowRight size={14} aria-hidden="true" />
        </span>
      </div>
    </button>
  );
};

const PostDialog = ({ post, apiUrl, open, onClose, t }) => {
  const { i18n } = useTranslation();
  const img = post ? buildImageUrl(apiUrl, post.cover_url) : null;
  if (!post) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto p-0"
        data-testid="news-detail-dialog"
      >
        {img && (
          <div className="w-full h-64 md:h-80 overflow-hidden">
            <img src={img} alt={post.title} className="w-full h-full object-cover" />
          </div>
        )}
        <div className="p-6 md:p-8">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-3">
              <span
                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider border ${
                  post.category === 'project'
                    ? 'bg-amber-100 text-amber-800 border-amber-200'
                    : 'bg-teal-100 text-teal-800 border-teal-200'
                }`}
              >
                {post.category === 'project' ? t('news.badgeProject') : t('news.badgeNews')}
              </span>
              <span className="text-xs text-gray-500 inline-flex items-center gap-1">
                <Calendar size={12} aria-hidden="true" />
                {formatDate(post.created_at, i18n.language)}
              </span>
            </div>
            <DialogTitle className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
              {post.title}
            </DialogTitle>
          </DialogHeader>
          <p className="text-lg text-gray-700 mt-4 leading-relaxed">{post.summary}</p>
          <div className="prose prose-teal max-w-none mt-6 text-gray-800 prose-headings:text-gray-900 prose-a:text-teal-600 prose-strong:text-gray-900">
            <ReactMarkdown>{post.content || ''}</ReactMarkdown>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const News = () => {
  const { t } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openPost, setOpenPost] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetch = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/news/`, { params: { limit: 9 } });
      setPosts(data);
    } catch (error) {
      console.error('Erro ao carregar notícias:', error);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  if (!loading && posts.length === 0) return null;

  return (
    <section
      id="news"
      className="py-20 bg-white"
      aria-labelledby="news-heading"
    >
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <span className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            <Newspaper size={14} aria-hidden="true" />
            {t('news.badge')}
          </span>
          <h2 id="news-heading" className="text-4xl font-bold text-gray-900 mb-4">
            {t('news.title')}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t('news.subtitle')}
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12" role="status">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
            <span className="sr-only">{t('news.loading')}</span>
          </div>
        ) : (
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            data-testid="news-grid"
          >
            {posts.map((post) => (
              <PostCard
                key={post.id}
                post={post}
                apiUrl={API_URL}
                onOpen={setOpenPost}
                t={t}
              />
            ))}
          </div>
        )}
      </div>

      <PostDialog
        post={openPost}
        apiUrl={API_URL}
        open={!!openPost}
        onClose={() => setOpenPost(null)}
        t={t}
      />
    </section>
  );
};

export default News;
